import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { IAddPaymentMethodBody } from '@makyoapp/makyo-sdk/api/Organizations'
import { AddressAdapter, initialInputState, InputState, PaymentMethodAdapter } from '@makyoapp/makyo-common'
import * as Icon from '@makyoapp/makyo-icon/icon'
import {
  Button,
  ButtonVariant,
  DeleteModal,
  Divider,
  Field,
  Label,
  Loading,
  Modal,
  ModalBorderVariant,
  ModalGrid,
  ModalHeaderProps,
  PaymentCard,
  SelectDropdown,
} from '@makyoapp/makyo-ui'

import {
  AddressFieldProps,
  AddressForm,
  AddressFormState,
  CardField,
  CVCField,
  ExpiryField,
  isDisableAddressFormButton,
  RequiredField,
} from '@/components'
import { PageUnauthorized } from '@/components/PageUnauthorized'
import { SaveButton } from '@/components/SaveButton'

import useAddPayment from './hooks/useAddPayment'
import usePaymentMethods from './hooks/usePaymentMethods'

interface AddPaymentModalProps {
  id: string
  children?: React.ReactNode
  onClose?: () => void
  show?: boolean
  onSubmit: (paymentMethod: IAddPaymentMethodBody, closeModal: () => void) => void
  headerProps?: ModalHeaderProps
  addresses: AddressAdapter[]
}

export interface FormState {
  card: InputState
  expiry: InputState
  cvc: InputState
  name: InputState
  addressId: InputState
}

export const initialFormState: FormState = {
  card: { ...initialInputState },
  expiry: { ...initialInputState },
  cvc: { ...initialInputState },
  name: { ...initialInputState },
  addressId: { ...initialInputState, disableButton: false },
}

export interface ChangePaymentMethodProps {
  onSelect: () => void
  cards: PaymentMethodAdapter[]
}

export function ChangePaymentMethodModal(props: ChangePaymentMethodProps) {
  return (
    <div className='grid lg:grid-cols-2 gap-4'>
      {props.cards.map((card, index) => {
        return (
          <button key={index} onClick={() => props.onSelect()}>
            <PaymentCard className={'cursor-pointer hover:border-primary-600'} {...card} isDefault={false} />
          </button>
        )
      })}
    </div>
  )
}

function ExistingAddressField({ id, state, setState, addresses }: AddressFieldProps & { addresses: AddressAdapter[] }) {
  const { t } = useTranslation()
  const [options] = useState<Array<string | { value: string; label: string }>>([
    { value: '#', label: t('Add New') },
    '#div#',
    `#{${t('Billing Address')}}#`,
    ...addresses.map((ad) => {
      return { value: ad.id, label: ad.title }
    }),
  ])

  const selected =
    options.find((o) => {
      return typeof o === 'string' ? o === state.value : o.value === state.value
    }) || options[0]

  return (
    <Field>
      <Label>{t('Address')}</Label>
      <SelectDropdown
        id={id}
        value={selected}
        onChange={(e) => {
          setState({ ...state, value: typeof e === 'string' ? e : e.value })
        }}
        optionLabel={(opt) => (typeof opt === 'string' ? opt : opt.label)}
        renderOption={(opt) => {
          if (typeof opt === 'string') {
            return opt
          }

          if (opt.value === '#') {
            return (
              <div className='flex-c'>
                <Icon.Plus16 />
                <span>{t('Add New')}</span>
              </div>
            )
          } else {
            return opt.label
          }
        }}
        menuHeadingClassName='px-2 py-2 text-m-sm text-zinc-400'
        options={options}
      />
    </Field>
  )
}

interface EditPaymentModalProps {
  id: string
  card: PaymentMethodAdapter
  addressState: AddressFormState
  setAddressState: (
    patch: Partial<AddressFormState> | ((prevState: AddressFormState) => Partial<AddressFormState>)
  ) => void
  onClose: () => void
  onEdit: (addressId: string) => void
  addresses: AddressAdapter[]
}

function EditPaymentModal({
  id,
  addressState,
  setAddressState,
  onEdit,
  onClose,
  addresses,
  card,
}: EditPaymentModalProps) {
  const { t } = useTranslation()
  const closeModal = () => {
    onClose()
  }

  const [addressId, setAddressId] = useState<InputState>({
    value: card.addressId,
    disableButton: false,
    error: null,
  })

  return (
    <Modal id={`${id}-modal`} show={true} onHide={closeModal}>
      <Modal.Header onCloseClick={closeModal}>{t('Edit Payment Method')}</Modal.Header>
      <Modal.Body>
        <div className='w-[320px] md:min-w-[480px]'>
          <ExistingAddressField
            id={`${id}-address`}
            state={addressId}
            setState={(addressId) => setAddressId(addressId)}
            addresses={addresses}
          />
          {addressId.value === '#' && (
            <>
              <Divider />
              <div className='text-m-sm uppercase text-zinc-400'>{t('New Address')}</div>
              <AddressForm id={`${id}-new-address`} state={addressState} setState={setAddressState} />
            </>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer className='justify-between'>
        <Button id={`${id}-cancel`} variant={ButtonVariant.Outlined} onClick={closeModal}>
          {t('Cancel')}
        </Button>
        <Button
          id={`${id}-submit`}
          onClick={() => {
            onEdit(addressId.value)
          }}
          disabled={addressId.value !== '#' ? addressId.disableButton : isDisableAddressFormButton(addressState)}
        >
          {t('Save')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

function AddPaymentModal({ id, onSubmit, onClose, show, headerProps, addresses }: AddPaymentModalProps) {
  const { t } = useTranslation()
  // States
  const {
    state,
    cardType,
    addressState,
    isLoading,
    saveButtonDisabled,
    setState,
    setCardType,
    setAddressState,
    handleSavePayment,
    closeModal,
  } = useAddPayment(addresses, onSubmit, onClose)

  return (
    <ModalGrid
      modalId={`${id}-modal`}
      show={show}
      onHide={() => onClose?.()}
      onCancel={closeModal}
      onConfirm={handleSavePayment}
      title={t('New Payment Method')}
      leadingTitle={t('Cancel')}
      primaryTrailing={t('Save')}
      disabled={saveButtonDisabled}
      isLoading={isLoading}
      cancelId={`${id}-cancel`}
      submitId={`${id}-submit`}
      innerClassName='rounded-sm overflow-hidden'
      borderVariant={ModalBorderVariant.Rounded}
    >
      <div className='w-full'>
        <CardField
          id={`${id}-card`}
          state={state.card}
          setState={(card) => setState({ card })}
          cardType={cardType}
          setCardType={setCardType}
        />
        <ExpiryField id={`${id}-expiry`} state={state.expiry} setState={(expiry) => setState({ expiry })} />
        <CVCField id={`${id}-cvc`} state={state.cvc} setState={(cvc) => setState({ cvc })} cardType={cardType} />
        <RequiredField
          id={`${id}-name`}
          className='rtl:[direction:ltr]'
          state={state.name}
          label={t('Name on card')}
          inputProps={{
            autoComplete: 'ccname',
            autoCorrect: 'off',
            spellCheck: false,
            'data-form-type': 'name,credit_card,payment',
          }}
          placeholder={t('Please enter name')}
          setState={(name) => setState({ name })}
        />
        <ExistingAddressField
          id={`${id}-address`}
          state={state.addressId}
          setState={(addressId) => setState({ addressId })}
          addresses={addresses}
        />
        {state.addressId.value === '#' && (
          <>
            <Divider />
            <div className='text-m-sm uppercase text-zinc-400'>{t('New Address')}</div>
            <AddressForm id={`${id}-new-address`} state={addressState} setState={setAddressState} />
          </>
        )}
      </div>
    </ModalGrid>
  )
}

export default function PaymentMethods() {
  const {
    showModal,
    addresses,
    isLoading,
    paymentMethods,
    defaultCard,
    cardToRemove,
    isBtnLoading,
    editCard,
    editAddress,
    currentOrg,
    canPm,
    setShowModal,
    handleAddPayment,
    makeDefault,
    handleEditCard,
    removeCard,
    handleHide,
    handleDelete,
    setEditCard,
    setEditAddress,
    t,
  } = usePaymentMethods()

  return !canPm ? (
    <PageUnauthorized />
  ) : (
    <div>
      <div>
        <Button
          id='add-pm-btn'
          className='text-m-m'
          variant={ButtonVariant.Outlined}
          onClick={() => setShowModal(true)}
          icon={<Icon.Plus16 />}
        >
          {t('Add Payment Method')}
        </Button>
        <AddPaymentModal
          id='add-pm'
          show={showModal}
          onClose={() => setShowModal(false)}
          addresses={addresses ?? []}
          onSubmit={handleAddPayment}
        />
      </div>
      {isLoading ? (
        <Loading />
      ) : (
        <div className='grid grid-cols-1 xl:grid-cols-2 gap-8'>
          {paymentMethods?.map(({ isDefault, ...card }, index) => {
            const now = new Date()
            const expiry = new Date(Number(`20${card.expires.year}`), card.expires.month - 1)
            const isExpired = now > expiry
            // TODO: addressId, and nameOnCard returned from SDK is empty

            return (
              <PaymentCard
                key={`card-${index}`}
                {...card}
                dateLabel={t('Expires')}
                expiredLabel={t('Expired')}
                defaultLabel={t('Default')}
                name={card.nameOnCard || 'Name is empty'}
                isDefault={defaultCard === card.id}
                menu={
                  <>
                    {!isExpired && defaultCard !== card.id && (
                      <PaymentCard.MenuItem
                        id={`${card.id}-default-act`}
                        className='pm-default-act'
                        onClick={() => makeDefault(card)}
                      >
                        <Icon.Star16 />
                        <span>{t('Make Default')}</span>
                      </PaymentCard.MenuItem>
                    )}
                    <PaymentCard.MenuItem
                      className='pm-edit-act'
                      id={`${card.id}-edit-act`}
                      onClick={() => handleEditCard(card, isDefault)}
                    >
                      <Icon.Edit16 />
                      <span>{t('Edit')}</span>
                    </PaymentCard.MenuItem>
                    {!isDefault && (
                      <PaymentCard.MenuItem
                        id={`${card.id}-delete-act`}
                        className='pm-delete-act red'
                        onClick={() => removeCard(card)}
                      >
                        <Icon.Trash16 />
                        <span>{t('Delete')}</span>
                      </PaymentCard.MenuItem>
                    )}
                  </>
                }
              />
            )
          })}
          {cardToRemove && (
            <DeleteModal
              show={true}
              onHide={handleHide}
              title={t('Delete Payment Method')}
              withCancel={true}
              cancelLabel={t('Cancel')}
              continueLabel={t('Yes, delete')}
              isLoading={isBtnLoading}
              onSubmit={handleDelete}
            >
              <div className='text-base mb-2'>{t("Are you sure you'd like to delete this payment method?")}</div>
              <div className='text-light modal-sm mb-4'>{t('removePaymentMethodDescription')}</div>
            </DeleteModal>
          )}
          {editCard && (
            <EditPaymentModal
              id='edit-pm'
              card={editCard}
              addresses={addresses ?? []}
              onClose={() => setEditCard(undefined)}
              addressState={editAddress}
              setAddressState={setEditAddress}
              onEdit={(addressId) => {
                if (currentOrg) {
                  alert('no sdk to update payment methods')
                  if (addressId === '#') {
                    /*OrgBillingAPI.ReqAddAddress(
                      currentOrg?.id,
                      toSdkAddressBodyWithoutId({
                        title: editAddress.title.value,
                        addressLine1: editAddress.addressLine1.value,
                        addressLine2: editAddress.addressLine2,
                        zip: editAddress.zip.value,
                        city: editAddress.city.value,
                        country: editAddress.country.value,
                        state: editAddress.state.value,
                        isDefault: addresses.length < 1,
                        phone: editAddress.phone
                      })
                    )
                      .then(() => {})
                      .catch(printAPIError)
                      .finally(() => {})*/
                  } else {
                    //
                  }
                }
              }}
            />
          )}
        </div>
      )}
    </div>
  )
}
